// useOverrideCapture.ts - Updated to handle WebSocket user removal
import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { UserInfoInterface } from 'Interfaces/User/userInterfaces';
import { toJS } from 'mobx';
import { useEffect, useState } from 'react';

interface OverrideItem {
  jobId: string;
  action: string;
  valueBefore: string | number;
  valueNow: string | number;
  reason: string;
}

// Hook parameters for WebSocket integration
interface UseOverrideCaptureParams {
  sendActiveUserUpdate?: (roomId: string, action: 'add' | 'remove') => void;
  isConnected?: boolean;
}

export const useOverridecapture = (
  sendActiveUserUpdate?: (roomId: string, action: 'add' | 'remove') => void,
  isConnected?: boolean
) => {
  const { GlobalModalStore, JobPasteStore, UserStore } = useStore();
  const { getCurrentInstance, toggleModal } = GlobalModalStore;
  const visibility = getCurrentInstance(MODAL_TYPE.PPS_JOB_PASTE_OVERRIDE_CAPTURE_MODAL);

  // Sample data for the table with valueBefore and valueNow
  const [items, setItems] = useState<OverrideItem[]>([]);

  // Function to update the reason for a specific item
  const updateReason = (index: number, reason: string) => {
    const updatedItems = [...items];
    updatedItems[index].reason = reason;
    setItems(updatedItems);
  };

  useEffect(() => {
    if (JobPasteStore.reasonCaptureForOverrides) {
      setItems(toJS(JobPasteStore.reasonCaptureForOverrides));
    }
  }, [JobPasteStore.reasonCaptureForOverrides, visibility]);

  // Validation function to check if all reasons are filled
  const isValid = () => {
    return items.every(item => item.reason.trim() !== '');
  };

  // Save function to handle the save action
  const handleSave = async () => {
    if (isValid()) {
      const userInfo = UserStore.userMetadata;
      JobPasteStore.addOverrideReasonToJobNotes(items, userInfo as UserInfoInterface);

      // Remove user from active users BEFORE saving since this is the final save action
      // This ensures user is only removed when the save operation actually completes
      if (isConnected && sendActiveUserUpdate && JobPasteStore.currentRoomInEditMode) {
        sendActiveUserUpdate(JobPasteStore.currentRoomInEditMode, 'remove');
      }

      await JobPasteStore.saveEditedRoomJobs(JobPasteStore.currentRoomInEditMode as string);

      onClose();
    } else {
      alert('Please provide a reason for all items');
    }
  };

  const onClose = () => toggleModal(MODAL_TYPE.PPS_JOB_PASTE_OVERRIDE_CAPTURE_MODAL);
  const isLoading = JobPasteStore.jobEditSaving;

  return {
    visibility,
    onClose,
    items,
    updateReason,
    isValid,
    handleSave,
    isLoading
  };
};
